import React, {
  useState,
  useRef,
  useCallback,
  KeyboardEvent,
  FormEvent,
} from 'react';
import { Segment, Command, Document, Person } from '../types';
import {
  removeSegment,
  segmentsToHtml,
  htmlToSegments,
  getCaretPosition,
  setCaretPosition,
} from '../utils';

interface UseEnhancedTextAreaProps {
  filteredSuggestions?: (Document | Command | Person)[];
  handleSend: (segments: Segment[]) => Promise<void> | void;
  disableEnterToSend?: boolean;
}

export const useEnhancedTextArea = ({
  filteredSuggestions: _filteredSuggestions = [],
  handleSend,
  disableEnterToSend = false,
}: UseEnhancedTextAreaProps) => {
  // Use state to allow external updates to filteredSuggestions
  const [filteredSuggestions, setFilteredSuggestionsState] =
    useState<(Document | Command | Person)[]>(_filteredSuggestions);

  // Use ref to track previous value and prevent unnecessary updates
  const prevFilteredSuggestionsRef = React.useRef(_filteredSuggestions);

  // Update when prop changes, but only if the array reference or contents actually changed
  React.useEffect(() => {
    // Compare by reference first (fast check)
    if (prevFilteredSuggestionsRef.current === _filteredSuggestions) {
      return;
    }

    // Compare by length and IDs to avoid unnecessary updates
    const prev = prevFilteredSuggestionsRef.current;
    const current = _filteredSuggestions;

    if (
      prev.length === current.length &&
      prev.every(
        (item, index) =>
          item.id === current[index]?.id && item.type === current[index]?.type
      )
    ) {
      // Contents are the same, just update the ref
      prevFilteredSuggestionsRef.current = _filteredSuggestions;
      return;
    }

    // Only update if contents actually changed
    prevFilteredSuggestionsRef.current = _filteredSuggestions;
    setFilteredSuggestionsState(_filteredSuggestions);
  }, [_filteredSuggestions]);

  // State
  const [segments, setSegments] = useState<Segment[]>([
    { type: 'text', content: '' },
  ]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [setRecentDocuments] = useState<Document[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeCommand, setActiveCommand] = useState<Command | null>(null);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [dropdownType, setDropdownType] = useState<
    'document' | 'person' | 'command'
  >('document');

  // Refs
  const contentEditableRef = useRef<HTMLDivElement | null>(null);
  const pendingCursorPositionRef = useRef<number | null>(null);
  const isInitialMountRef = useRef(true);

  const removeItem = (index: number) => {
    removeSegment(segments, index, setSegments);
  };

  const isOnlyTextSegment = () => {
    return segments.length === 1 && segments[0].type === 'text';
  };

  const isInsideMention = (): boolean => {
    if (!contentEditableRef.current) return false;

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return false;

    let node: Node | null = selection.getRangeAt(0).startContainer;

    // Traverse up to check if we're inside a mention span
    while (node && node !== contentEditableRef.current) {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        ((node as HTMLElement).classList?.contains('document-reference') ||
          (node as HTMLElement).classList?.contains('person-reference'))
      ) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };

  const getCursorInfo = () => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    const offset = range.startOffset;

    if (node.nodeType === Node.TEXT_NODE) {
      return {
        offset,
        text: node.textContent || '',
      };
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const childBefore = offset > 0 ? node.childNodes[offset - 1] : null;
      if (childBefore?.nodeType === Node.TEXT_NODE) {
        const text = childBefore.textContent || '';
        return { offset: text.length, text };
      }

      const childAt = node.childNodes[offset];
      if (childAt?.nodeType === Node.TEXT_NODE) {
        return { offset: 0, text: childAt.textContent || '' };
      }
    }

    return null;
  };

  const findTriggerAtCursor = (): {
    triggerChar: '@' | '#';
    searchText: string;
  } | null => {
    const cursorInfo = getCursorInfo();
    if (!cursorInfo) {
      return null;
    }

    const { text, offset } = cursorInfo;

    for (let i = offset - 1; i >= 0; i--) {
      const char = text[i];

      if (char === '@' || char === '#') {
        const prevChar = i > 0 ? text[i - 1] : ' ';
        if (prevChar === ' ' || prevChar === '\n' || i === 0) {
          return {
            triggerChar: char,
            searchText: text.substring(i + 1, offset),
          };
        }
      }

      if (char === ' ' || char === '\n') {
        break;
      }
    }

    return null;
  };

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setSearchTerm('');
    setActiveCommand(null);
    setSelectedIndex(0);
  }, []);

  const syncDropdownToCaret = () => {
    if (isInsideMention()) {
      closeDropdown();
      return;
    }

    const triggerContext = findTriggerAtCursor();
    if (triggerContext) {
      const { triggerChar, searchText } = triggerContext;
      setDropdownType(triggerChar === '@' ? 'person' : 'document');
      setSearchTerm(searchText);
      setShowDropdown(true);
      setSelectedIndex(0);

      if (triggerChar === '#') {
        setActiveCommand(null);
      }
      return;
    }

    closeDropdown();
  };

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    if (!element) return;

    // Save cursor position before updating segments
    const cursorPosition = getCaretPosition(element);
    pendingCursorPositionRef.current = cursorPosition;

    // Parse HTML back to segments
    const newSegments = htmlToSegments(element.innerHTML);
    syncDropdownToCaret();
    setSegments(newSegments);
  };

  // Restore cursor position after segments update
  React.useEffect(() => {
    if (
      pendingCursorPositionRef.current !== null &&
      contentEditableRef.current &&
      !isInitialMountRef.current
    ) {
      const position = pendingCursorPositionRef.current;
      pendingCursorPositionRef.current = null;
      
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (contentEditableRef.current) {
          setCaretPosition(contentEditableRef.current, position);
        }
      });
    }
    isInitialMountRef.current = false;
  }, [segments]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!contentEditableRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    // Handle backspace to delete mentions
    if (e.key === 'Backspace' && !showDropdown) {
      let mentionToDelete: HTMLElement | null = null;

      // Check if cursor is at the start of a text node, check previous sibling
      if (
        range.startOffset === 0 &&
        range.startContainer.nodeType === Node.TEXT_NODE
      ) {
        const textNode = range.startContainer;
        const parent = textNode.parentElement;
        if (parent && parent === contentEditableRef.current) {
          // We're at the start of a direct text child, check previous sibling
          const prevSibling = textNode.previousSibling;
          if (
            prevSibling &&
            prevSibling.nodeType === Node.ELEMENT_NODE &&
            ((prevSibling as HTMLElement).classList.contains(
              'document-reference'
            ) ||
              (prevSibling as HTMLElement).classList.contains(
                'person-reference'
              ))
          ) {
            mentionToDelete = prevSibling as HTMLElement;
          }
        } else if (parent) {
          // We're in a nested text node, check parent's previous sibling
          const prevSibling = parent.previousSibling;
          if (
            prevSibling &&
            prevSibling.nodeType === Node.ELEMENT_NODE &&
            ((prevSibling as HTMLElement).classList.contains(
              'document-reference'
            ) ||
              (prevSibling as HTMLElement).classList.contains(
                'person-reference'
              ))
          ) {
            mentionToDelete = prevSibling as HTMLElement;
          }
        }
      } else if (range.startOffset === 0) {
        // Cursor is at the start of an element, check previous sibling
        const startNode = range.startContainer;
        const nodeToCheck =
          startNode.nodeType === Node.ELEMENT_NODE
            ? (startNode as HTMLElement)
            : startNode.parentElement;
        if (nodeToCheck) {
          const prevSibling = nodeToCheck.previousSibling;
          if (prevSibling && prevSibling.nodeType === Node.ELEMENT_NODE) {
            const prevElement = prevSibling as HTMLElement;
            if (
              prevElement.classList.contains('document-reference') ||
              prevElement.classList.contains('person-reference')
            ) {
              mentionToDelete = prevElement;
            }
          }
        }
      }

      // Check if cursor is inside a mention (shouldn't happen, but handle it)
      if (!mentionToDelete) {
        const mentionSpanInside =
          container.nodeType === Node.ELEMENT_NODE
            ? (container as HTMLElement).closest(
                '.document-reference, .person-reference'
              )
            : container.parentElement?.closest(
                '.document-reference, .person-reference'
              );
        if (mentionSpanInside && mentionSpanInside instanceof HTMLElement) {
          mentionToDelete = mentionSpanInside;
        }
      }

      if (mentionToDelete) {
        e.preventDefault();
        // Remove the mention directly from DOM
        const nextSibling = mentionToDelete.nextSibling;
        const prevSibling = mentionToDelete.previousSibling;
        mentionToDelete.remove();

        // Update segments
        const newSegments = htmlToSegments(
          contentEditableRef.current.innerHTML
        );
        setSegments(newSegments);

        // Place cursor where mention was
        const newRange = document.createRange();
        const newSel = window.getSelection();
        if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
          newRange.setStart(nextSibling, 0);
        } else if (prevSibling && prevSibling.nodeType === Node.TEXT_NODE) {
          newRange.setStartAfter(prevSibling);
        } else if (nextSibling) {
          newRange.setStartBefore(nextSibling);
        } else {
          // Create a text node if needed
          const textNode = document.createTextNode('');
          contentEditableRef.current.appendChild(textNode);
          newRange.setStart(textNode, 0);
        }
        newRange.collapse(true);
        newSel?.removeAllRanges();
        newSel?.addRange(newRange);
        return;
      }
    }

    // Prevent editing inside mentions
    if (contentEditableRef.current) {
      const mentionSpan =
        container.nodeType === Node.ELEMENT_NODE
          ? (container as HTMLElement).closest(
              '.document-reference, .person-reference'
            )
          : (container.parentElement?.closest(
              '.document-reference, .person-reference'
            ) as HTMLElement | null);

      if (mentionSpan) {
        // If trying to type inside a mention, move cursor outside
        if (
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey &&
          e.key !== 'Backspace'
        ) {
          e.preventDefault();
          const newRange = document.createRange();
          newRange.setStartAfter(mentionSpan);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          // Insert the character after the mention
          const textNode = document.createTextNode(e.key);
          newRange.insertNode(textNode);
          newRange.setStartAfter(textNode);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          // Trigger input event manually
          const inputEvent = new Event('input', { bubbles: true });
          contentEditableRef.current.dispatchEvent(inputEvent);
          return;
        }
        // Arrow keys: move cursor outside mention
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          const newRange = document.createRange();
          if (e.key === 'ArrowLeft') {
            newRange.setStartBefore(mentionSpan);
          } else {
            newRange.setStartAfter(mentionSpan);
          }
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          return;
        }
      }
    }

    if (!showDropdown) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (disableEnterToSend) {
          return;
        }
        void handleSendMessage();
      }
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      requestAnimationFrame(() => syncDropdownToCaret());
      return;
    }

    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        setSelectedIndex((prev) =>
          e.shiftKey
            ? (prev - 1 + filteredSuggestions.length) %
              filteredSuggestions.length
            : (prev + 1) % filteredSuggestions.length
        );
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredSuggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(
          (prev) =>
            (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
          insertItem(filteredSuggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        closeDropdown();
        break;
    }
  };

  // Handle keyboard events from the search input in the dropdown
  const handleDropdownKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) {
      return;
    }

    switch (e.key) {
      case 'Tab':
        // Keep focus "locked" in the dropdown while it's open
        e.preventDefault();
        if (filteredSuggestions.length === 0) return;
        setSelectedIndex((prev) =>
          e.shiftKey
            ? (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length
            : (prev + 1) % filteredSuggestions.length
        );
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (filteredSuggestions.length === 0) return;
        setSelectedIndex((prev) => (prev + 1) % filteredSuggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (filteredSuggestions.length === 0) return;
        setSelectedIndex(
          (prev) =>
            (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
          insertItem(filteredSuggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        // Return focus to contenteditable
        if (contentEditableRef.current) {
          contentEditableRef.current.focus();
        }
        break;
    }
  };

  const handleSendMessage = async () => {
    // Read segments from the DOM at send time so we never miss document refs
    // (React state can be stale right after inserting a # document)
    const segmentsToSend = contentEditableRef.current
      ? htmlToSegments(contentEditableRef.current.innerHTML)
      : [...segments];

    const hasContent = segmentsToSend.some(
      (seg) =>
        (seg.type === 'text' && seg.content.trim()) ||
        seg.type === 'document' ||
        seg.type === 'person'
    );

    if (!hasContent) return;

    // Clear immediately before sending
    const emptySegments: Segment[] = [{ type: 'text', content: '' }];
    setSegments(emptySegments);
    // Clear contenteditable HTML immediately
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = '';
    }

    setIsLoadingResponse(true);
    try {
      await handleSend(segmentsToSend);
    } catch (error) {
      console.error('Error sending message:', error);
      // Optionally restore segments on error, but for now we'll keep it cleared
    } finally {
      setIsLoadingResponse(false);
    }
  };

  const insertItem = (item: Document | Command | Person) => {
    if (!contentEditableRef.current) return;

    // Restore focus to contentEditable first
    contentEditableRef.current.focus();

    // Determine trigger character based on dropdown type
    const triggerChar = dropdownType === 'person' ? '@' : '#';

    // Find the last occurrence of the trigger in the text
    // We need to account for existing mentions, so we'll search in the HTML
    let triggerPos = -1;
    let textNode: Node | null = null;
    let offsetInNode = 0;

    // Walk through the DOM to find where the trigger is
    const walker = document.createTreeWalker(
      contentEditableRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );

    let charCount = 0;
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const nodeText = node.textContent || '';
      const nodeLength = nodeText.length;

      // Check if trigger is in this node
      const triggerIndex = nodeText.lastIndexOf(triggerChar);
      if (triggerIndex >= 0) {
        // Check if there's text after the trigger (the search term)
        const textAfterTrigger = nodeText.substring(triggerIndex + 1);
        // If the search term matches what comes after the trigger, this is our node
        if (textAfterTrigger.startsWith(searchTerm)) {
          triggerPos = charCount + triggerIndex;
          textNode = node;
          offsetInNode = triggerIndex + 1 + searchTerm.length;
          break;
        }
      }
      charCount += nodeLength;
    }

    // If we didn't find it by walking, try a simpler approach
    if (!textNode) {
      // Find the last text node that contains the trigger
      const allTextNodes: Node[] = [];
      const collectTextNodes = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          allTextNodes.push(node);
        } else {
          node.childNodes.forEach(collectTextNodes);
        }
      };
      collectTextNodes(contentEditableRef.current);

      // Search backwards through text nodes
      for (let i = allTextNodes.length - 1; i >= 0; i--) {
        const node = allTextNodes[i];
        const nodeText = node.textContent || '';
        const triggerIndex = nodeText.lastIndexOf(triggerChar);
        if (triggerIndex >= 0) {
          triggerPos = triggerIndex;
          textNode = node;
          offsetInNode = triggerIndex + 1 + searchTerm.length;
          break;
        }
      }
    }

    if (!textNode || triggerPos === -1) {
      console.warn('Could not find trigger position for insertion');
      return;
    }

    // We now have the textNode and triggerPos
    // Get the text from the node
    const text = textNode.textContent || '';
    const actualTriggerPos = triggerPos; // This is the position in the node
    const cursorPos = offsetInNode; // Position after trigger + searchTerm

    // Split the text
    const beforeText = text.substring(0, actualTriggerPos);
    const afterText = text.substring(cursorPos);

    // Handle command differently - just keep the # and remove search text
    if (item.type === 'command') {
      const commandItem = item as Command;
      setActiveCommand(commandItem);
      // Replace the text node content
      textNode.textContent = beforeText + triggerChar + afterText;
      // Set cursor after the #
      const newRange = document.createRange();
      const newSel = window.getSelection();
      newRange.setStart(textNode, actualTriggerPos + 1);
      newRange.collapse(true);
      newSel?.removeAllRanges();
      newSel?.addRange(newRange);
      closeDropdown();
      contentEditableRef.current.focus();
      return;
    }

    // Create mention element
    const mentionSpan = document.createElement('span');
    mentionSpan.className =
      item.type === 'person' ? 'person-reference' : 'document-reference';
    mentionSpan.setAttribute('contenteditable', 'false');
    mentionSpan.setAttribute('spellcheck', 'false');
    mentionSpan.setAttribute('data-type', item.type);
    mentionSpan.setAttribute('data-id', String(item.id));

    if (item.type === 'person') {
      mentionSpan.setAttribute('data-user-id', (item as Person).userId);
      mentionSpan.setAttribute('data-name', item.name);
      mentionSpan.textContent = `@${item.name}`;
    } else {
      mentionSpan.setAttribute('data-name', item.name);
      mentionSpan.textContent = `[${item.name}]`;
    }

    // Create text nodes
    const beforeNode = document.createTextNode(beforeText);
    const afterNode = document.createTextNode(' ' + afterText);

    // Replace the text node with our new structure
    const parent = textNode.parentNode;
    if (!parent) return;

    parent.insertBefore(beforeNode, textNode);
    parent.insertBefore(mentionSpan, textNode);
    parent.insertBefore(afterNode, textNode);
    parent.removeChild(textNode);

    // Set cursor after mention (after the space)
    const newRange = document.createRange();
    const newSel = window.getSelection();
    newRange.setStart(afterNode, 1);
    newRange.collapse(true);
    newSel?.removeAllRanges();
    newSel?.addRange(newRange);

    // Update segments from HTML
    const newSegments = htmlToSegments(contentEditableRef.current.innerHTML);
    setSegments(newSegments);

    closeDropdown();

    if (item.type === 'document') {
      setActiveCommand(null);
      setRecentDocuments((prev) => {
        const filtered = prev.filter((d) => d.id !== (item as Document).id);
        return [item as Document, ...filtered].slice(0, 5);
      });
    }

    contentEditableRef.current.focus();
  };

  // Expose method to insert document programmatically
  const insertDocument = (document: Document) => {
    insertItem(document);
  };

  // Handle click on contenteditable to remove references or move cursor outside mentions
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target || !contentEditableRef.current) return;

    // Check if clicked element is a document or person reference
    const isDocumentRef =
      target.classList.contains('document-reference') ||
      target.closest('.document-reference');
    const isPersonRef =
      target.classList.contains('person-reference') ||
      target.closest('.person-reference');

    if (isDocumentRef || isPersonRef) {
      const refElement = (
        isDocumentRef
          ? target.closest('.document-reference')
          : target.closest('.person-reference')
      ) as HTMLElement;
      if (!refElement) return;

      // If double-click, remove the mention
      if (e.detail === 2) {
        const dataId = refElement.getAttribute('data-id');
        if (!dataId) return;

        // Find and remove the segment
        const currentSegments = htmlToSegments(
          contentEditableRef.current.innerHTML
        );
        const indexToRemove = currentSegments.findIndex(
          (seg) =>
            (seg.type === 'document' && String(seg.id) === dataId) ||
            (seg.type === 'person' && seg.id === dataId)
        );

        if (indexToRemove >= 0) {
          removeSegment(currentSegments, indexToRemove, setSegments);
          // Update HTML after removal
          setTimeout(() => {
            if (contentEditableRef.current) {
              const newSegments = htmlToSegments(
                contentEditableRef.current.innerHTML
              );
              const updatedSegments = [...newSegments];
              updatedSegments.splice(indexToRemove, 1);
              if (updatedSegments.length === 0) {
                updatedSegments.push({ type: 'text', content: '' });
              }
              setSegments(updatedSegments);
              contentEditableRef.current.innerHTML =
                segmentsToHtml(updatedSegments);
              // Move cursor to where the mention was
              const newCursorPos = getCaretPosition(contentEditableRef.current);
              setCaretPosition(contentEditableRef.current, newCursorPos);
            }
          }, 0);
        }
      } else {
        // Single click: move cursor to after the mention (prevent editing inside)
        e.preventDefault();
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.setStartAfter(refElement);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
  };

  return {
    // State
    segments,
    showDropdown,
    searchTerm,
    activeCategory,
    activeCommand,
    isLoadingResponse,
    selectedIndex,
    dropdownType,

    // Refs
    contentEditableRef,

    // Methods
    handleInput,
    handleKeyDown,
    handleDropdownKeyDown,
    handleContentClick,
    removeItem,
    insertItem,
    insertDocument,
    setSearchTerm,
    setActiveCategory,
    isOnlyTextSegment,
    handleSendMessage,
    setFilteredSuggestions: setFilteredSuggestionsState,
  };
};
